// GRWM's script as the form reads it (spec G14-grwm, "The draft", PR D; the playbook's Script,
// L9399-9406): the ten beats and their names, the blank script the pencil fills, the meta line,
// and what keeps the script from approval, in the words and at the numbers the server refuses on
// Approve (`templates/grwm_script.py`, GRWM-10).
import { GrwmBeat, type GrwmScript } from "../../../api/generated/model";
import { countWords } from "./podcast-script";

/** Our words (no live equivalent, GRWM-13), as the server's. */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const NAME_THE_PRODUCT = "Say the product's name: her entryway line names it.";
export const GENERATE_SCRIPT = "Generate or write the script";
export const STILLS_PENDING = "Wait for all sixteen images";
export const EMPTY_LINE = "Every beat needs its line.";
export const OUT_OF_ORDER = "The script needs its ten beats in order.";
export const NO_ACTION = "Say what she does with the product in the entryway beat.";

/** The ten beats in speaking order, as the server names them. */
export const BEATS: readonly GrwmBeat[] = Object.values(GrwmBeat);
export const BEAT_NAMES: Readonly<Record<GrwmBeat, string>> = {
  shower: "shower",
  skin_prep: "skin prep",
  blush_lipstick: "blush and lipstick",
  curls: "curls",
  dress_reveal: "dress reveal",
  entryway: "entryway",
  leaving: "leaving",
  nerves: "nerves",
  arrival: "arrival",
  toast: "toast",
};
/** About 70 words, about 7 a 3 s beat (L9401): Approve's bounds and a line's most. */
const MIN_WORDS = 50;
const MAX_WORDS = 95;
export const MAX_LINE_WORDS = 14;
const BEAT_SECONDS = 3;

function scriptWords(lines: GrwmScript["lines"]): number {
  return lines.reduce((total, line) => total + countWords(line.text), 0);
}

/** The meta line: "10 lines · 72 words (50–95) · ≈ 30 s", ten 3 s beats in two 15 s clips. */
export function scriptMeta(lines: GrwmScript["lines"]): string {
  return `${lines.length} lines · ${scriptWords(lines)} words (${MIN_WORDS}–${MAX_WORDS}) · ≈ ${lines.length * BEAT_SECONDS} s`;
}

/** "Write the 10 lines yourself": a blank line a beat and no entryway action. */
export function blankScript(): GrwmScript {
  return { lines: BEATS.map((beat) => ({ beat, text: "" })), product_action: "" };
}

/** "Use the example script": the playbook's approved lines, with the entryway line and action
 * left for the member, since the example's are about her handbag and name no product (GRWM-21). */
export function exampleToFinish(example: GrwmScript): GrwmScript {
  return {
    lines: example.lines.map((line) => (line.beat === GrwmBeat.entryway ? { ...line, text: "" } : line)),
    product_action: "",
  };
}

/** What keeps the script from approval, as Approve refuses it, or null. */
export function scriptIssue(script: GrwmScript | null): string | null {
  if (script === null) return GENERATE_SCRIPT;
  if (script.lines.length !== BEATS.length) return EMPTY_LINE;
  if (script.lines.some((line, at) => line.beat !== BEATS[at])) return OUT_OF_ORDER;
  for (const line of script.lines) {
    if (line.text.trim() === "") return EMPTY_LINE;
    if (countWords(line.text) > MAX_LINE_WORDS) {
      return `Shorten the ${BEAT_NAMES[line.beat]} line: a beat is about 3 seconds.`;
    }
  }
  const total = scriptWords(script.lines);
  if (total < MIN_WORDS || total > MAX_WORDS) return `The script needs about 70 words: ${total} now.`;
  return script.product_action.trim() === "" ? NO_ACTION : null;
}
